import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { CardTitleIcon } from '@/components/CardTitleIcon';
import { LightboxShell } from '@/components/LightboxShell';
import type { Project } from '@/types';

/**
 * 專案刪除確認框(從 `ProjectListPage` 抽出的純結構重構,行為不變)。
 *
 * R393(CIS「所有彈框必須支援 ESC + 點遮罩 + 關閉鈕」)— 原本是自刻的 fixed 遮罩,
 * 三種關閉出口**一個都沒有**(只能按「取消」)。改包共用燈箱外框 {@link LightboxShell}
 * 的 `narrow`:ESC(分層)/ 遮罩 / 右上關閉鈕 + 焦點管理一次到位,三個出口都走 `onCancel`
 * (= 不刪)。刪除鈕 = 實心玫瑰 + 白字(CIS 刪除;原本是品牌橘 `--coral-500`,橘只做品牌識別)。
 *
 * ⚠️ 關閉鈕的名稱用「關閉」不用「取消」—— 遮罩與關閉鈕都吃 `closeTitle`,跟底部「取消」
 * 同名的話 `getByRole('button', { name: '取消' })` 一次撈到三顆。
 */
export function ProjectDeleteDialog({
  project,
  onCancel,
  onConfirm,
}: {
  project: Project;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation('projects');
  const { t: tCommon } = useTranslation('common');
  return (
    <LightboxShell
      size="narrow"
      ariaLabel={t('list.deleteConfirm.title')}
      closeTitle={tCommon('actions.close')}
      onClose={onCancel}
      data-testid="project-delete-dialog"
      head={
        <div style={headStyle}>
          {/* D3 `openDelProject()` 的 `.del-ic`:垃圾桶。R411 — 形狀走 CardTitleIcon(原 34px 玫瑰底方塊),
              顏色保留刪除類語意(`tone="danger"`) */}
          <CardTitleIcon name="trash" tone="danger" />
          <h2 id="project-delete-title" style={titleStyle}>
            {t('list.deleteConfirm.title')}
          </h2>
        </div>
      }
      footer={
        <div style={footRowStyle}>
          <button type="button" className="hai-btn-ghost" onClick={onCancel} data-testid="project-delete-cancel">
            {t('list.deleteConfirm.cancel')}
          </button>
          <button type="button" onClick={onConfirm} style={dangerBtnStyle} data-testid="project-delete-confirm">
            {t('list.deleteConfirm.confirm')}
          </button>
        </div>
      }
    >
      <p style={bodyTextStyle}>{t('list.deleteConfirm.body', { name: project.name })}</p>
    </LightboxShell>
  );
}

const headStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 };

const titleStyle: CSSProperties = { margin: 0, fontSize: 15, fontWeight: 800, color: 'var(--text-strong)' };

/** D3 `.del-d`:13px / 1.7 / 次文字。 */
const bodyTextStyle: CSSProperties = {
  margin: 0,
  color: 'var(--ink-500)',
  fontSize: 13,
  lineHeight: 1.7,
};

/** footer 兩顆鈕靠右(`LightboxShell` 的 foot 是 flex + gap 8)。 */
const footRowStyle: CSSProperties = { marginLeft: 'auto', display: 'flex', gap: 8 };

/** 實心玫瑰 #E11D48 + 白字 4.7:1(CIS 刪除鈕;D3 `.del-btn-danger`)。幾何同 `.btn-solid`。 */
const dangerBtnStyle: CSSProperties = {
  height: 34,
  padding: '0 14px',
  borderRadius: 8,
  border: 'none',
  background: 'var(--rose-600)',
  color: 'var(--on-accent)',
  fontSize: 12.5,
  fontWeight: 700,
  fontFamily: 'inherit',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
};
